import React, {useEffect} from 'react';
import {CircularProgress, Typography, Grid, Container} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import FadeIn from 'react-fade-in';
import {APICall} from '../../../api/api';
import DecilesTable from './../DecilesTable';
import CMPvisualiser from './../CMPvisualiser';
import {PeriodPicker} from './../PeriodPicker';
import useStore from '../../../store/store';
import useSnackbarSuccessOrError from './../subscription/customerSuccessBar';
import BidderSelect from './BidderSelect';
import ModeSwitch from './ModeSwitch';
import TotalsSwitch from './TotalsSwitch';
import SectionHeader from './SectionHeader';
import {
  calculateDecilesDataForGraph,
  calculateCumulativeDistributionData,
  extractBiddersFromData,
} from './dataHelpers';

const tooltipConfig = {
  revenueDistribution: {
    deciles: `Revenues per deciles are allocated according to each impression eCPM value. The number of impressions is similar in each decile, however, the eCPM ranges can be different. Data gathered from supply-side platforms, excluding Google Ad Exchange.`,
    fixedIntervals: `Revenues per fixed interval are counted as sum of all impressions' winning bid values that fall into particular eCPM interval. The intervals remain fixed while the distribution of impressions may change. Data is gathered from all demand excluding Google Ad Exchange.`,
    readMore: 'https://setupad.com/blog/avarage-ecpm-missleading-for-publishers/',
  },
  cumulativeDistribution:
    'The cumulative result of revenues and impressions divided into two parts. One below the selected CPM price (left side) and another above it (right side). Data gathered from supply-side platforms, excluding Google Ad Exchange.',
};

const useStyles = makeStyles(() => ({
  controlsContainer: {
    marginBottom: 15,
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  centerContent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
  },
}));

const PricesReport = React.memo(
  ({login, selectedBidder: initialSelectedBidder = 'Combined data', hideControls = false}) => {
    const classes = useStyles();
    const [callSnackbarSuccessError] = useSnackbarSuccessOrError();
    const [intervalsMode, setInvervalsMode] = React.useState(1); // 1 for fixed, 0 for deciles
    const [selectedBidder, setSelectedBidder] = React.useState(initialSelectedBidder);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [customLoadingStatus, setCustomLoadingStatus] = React.useState(null);
    const [calculatedState, setCalculatedState] = React.useState({
      graphData: false,
      cumulativeData: false,
      bidders: null,
    });

    const [showCombinedRevenue, setShowCombinedRevenue] = React.useState(true);
    const calculated = useStore(state => state.calculated);
    const selection = useStore(state => state.selection);
    const period = useStore(state => state.period);
    const pricesReportData = useStore(state => state.pricesReportData);
    const pricesReportCacheKey = useStore(state => state.pricesReportCacheKey);
    const setState = useStore(state => state.setState);

    // Local helper function to calculate eCPM for selected bidder
    const calculateECPM = (calculatedStore, decilesData = null, bidder = null) => {
      // If decilesData and bidder are provided, calculate from that data
      if (decilesData && bidder) {
        // Filter data for the selected bidder
        const filteredData = decilesData.filter(row => {
          const rowBidder = row.Dimension.CUSTOM_CRITERIA.split('=')[1].toLowerCase();

          if (bidder === 'Combined data' || bidder === 'bidder') {
            return true;
          } else if (bidder === 'Client Side Prebid') {
            return !rowBidder.includes('s2s');
          } else if (bidder === 'Server Side Prebid') {
            return rowBidder.includes('s2s');
          } else {
            return rowBidder === bidder.toLowerCase();
          }
        });

        // Calculate total revenue and impressions for the selected bidder
        const totalRevenue = filteredData.reduce((sum, row) => {
          return (
            sum +
            parseFloat(
              row.Column?.TOTAL_LINE_ITEM_LEVEL_CPM_AND_CPC_REVENUE ||
                row.TOTAL_LINE_ITEM_LEVEL_CPM_AND_CPC_REVENUE ||
                0
            ) /
              1000000
          );
        }, 0);

        const totalImpressions = filteredData.reduce((sum, row) => {
          return sum + parseInt(row.Column.TOTAL_LINE_ITEM_LEVEL_IMPRESSIONS);
        }, 0);

        if (totalImpressions === 0) {
          return '0.00';
        }

        return ((totalRevenue / totalImpressions) * 1000).toFixed(2);
      }

      // Fallback to original calculation
      if (!calculatedStore || !calculatedStore.rev || !calculatedStore.paidImp) {
        return '0.00';
      }
      return ((calculatedStore.rev / calculatedStore.paidImp) * 1000).toFixed(2);
    };

    // Calculate eCPM once and memoize it
    const eCPM = React.useMemo(() => {
      return calculateECPM(calculated, pricesReportData?.data, selectedBidder);
    }, [calculated, pricesReportData?.data, selectedBidder]);

    // Helper function to calculate all data at once
    const calculateAllData = (reportData, bidder, mode, calculatedStore) => {
      if (!reportData?.data || !calculatedStore) {
        return {
          graphData: false,
          cumulativeData: false,
          bidders: null,
        };
      }

      const graphData = calculateDecilesDataForGraph(reportData.data, bidder, mode);

      const cumulativeData = calculateCumulativeDistributionData(
        reportData.data,
        bidder,
        calculateECPM(calculatedStore, reportData.data, bidder)
      );
      const bidders = extractBiddersFromData(reportData.data);

      // Add bidders from directSSPCodeAndAlias only if they exist in the data
      if (reportData.directSSPCodeAndAlias) {
        Object.keys(reportData.directSSPCodeAndAlias).forEach(sspCode => {
          const biddersFromData = reportData.data.map(
            row => row.Dimension.CUSTOM_CRITERIA.split('=')[1]
          );
          if (biddersFromData.includes(sspCode) && !bidders.includes(sspCode)) {
            bidders.push(sspCode);
          }
        });
      }

      return {
        graphData,
        cumulativeData,
        bidders,
      };
    };

    // Fetch data when selection or period changes
    useEffect(() => {
      const currentParams = `${JSON.stringify(selection.value)}_${JSON.stringify(period)}`;

      // Skip if we already have data for these exact params
      if (pricesReportData && pricesReportCacheKey === currentParams) {
        return;
      }

      setLoading(true);
      setError(null);
      // Only clear data if parameters actually changed
      if (pricesReportCacheKey !== currentParams) {
        setState('pricesReportData', null);
      }
      setState('pricesReportCacheKey', currentParams);
      getDecilesData();
    }, [selection.value, period]);

    // Recalculate when data changes
    useEffect(() => {
      if (pricesReportData) {
        const newCalculatedState = calculateAllData(
          pricesReportData,
          selectedBidder,
          intervalsMode,
          calculated
        );
        setCalculatedState(newCalculatedState);
      }
    }, [pricesReportData, selectedBidder, intervalsMode, calculated]);

    const getDecilesData = async () => {
      try {
        let data = await APICall({
          retryOnTimeout: true,
          returnErrorResponse: true,
          endpoint: 'getPrebidReport',
          onRetry: () => {
            setCustomLoadingStatus(
              `Please wait for a while longer, we are processing a lot of data...`
            );
          },
          options: {
            method: 'POST',
            body: JSON.stringify({
              selection: selection,
              period: period,
              type: 'prices',
            }),
            headers: {
              Authorization: `Bearer ${login.token}`,
              'Cache-Control': 'max-age=0, no-cache',
              'Content-Type': 'application/json',
            },
          },
        });

        setLoading(false);
        setCustomLoadingStatus(null);

        if (data.success) {
          setState('pricesReportData', {
            data: data.data,
            attributeGroups: data.attributeGroups,
            directSSPCodeAndAlias: data.directSSPCodeAndAlias,
          });
        } else {
          setError(data.error);
          callSnackbarSuccessError(false, '', data.error);
        }
      } catch (error) {
        setLoading(false);
        setCustomLoadingStatus(null);
        setError('Failed to fetch data');
        callSnackbarSuccessError(false, '', 'Failed to fetch data');
      }
    };

    const handleBidderChange = change => {
      setSelectedBidder(change.value);
      // The useEffect will handle recalculation when selectedBidder changes
    };

    const handleModeChange = newMode => {
      setInvervalsMode(newMode);
    };

    return (
      <Container maxWidth="xl">
        <Grid xs={12} className={classes.centerContent}>
          {loading && (
            <FadeIn>
              <div className={classes.loadingContent}>
                <Typography variant={'caption'}>
                  {customLoadingStatus || 'Loading revenue distribution'}
                </Typography>

                <CircularProgress color="primary" />
              </div>
            </FadeIn>
          )}
          {error && <Typography variant={'caption'}>{error}</Typography>}
        </Grid>

        {calculatedState.graphData && calculatedState.cumulativeData && (
          <>
            <FadeIn>
              <Grid container spacing={2}>
                <Grid item md={12} lg={12} xl={12}>
                  <SectionHeader
                    title="Revenue distribution"
                    tooltip={
                      intervalsMode === 0
                        ? tooltipConfig.revenueDistribution.deciles
                        : tooltipConfig.revenueDistribution.fixedIntervals
                    }
                    readMoreUrl={tooltipConfig.revenueDistribution.readMore}
                  />

                  {!hideControls && (
                    <Grid item xs={12} className={classes.controlsContainer}>
                      <PeriodPicker isMobile={false} />
                    </Grid>
                  )}

                  {!hideControls && (
                    <BidderSelect
                      bidders={calculatedState.bidders}
                      selectedBidder={selectedBidder}
                      onBidderChange={handleBidderChange}
                      attributeGroups={pricesReportData?.attributeGroups}
                      directSSPCodeAndAlias={pricesReportData?.directSSPCodeAndAlias}
                    />
                  )}

                  <ModeSwitch
                    intervalsMode={intervalsMode}
                    onModeChange={handleModeChange}
                    removeMargin={hideControls}
                  />

                  <TotalsSwitch
                    selectedBidder={selectedBidder}
                    intervalsMode={intervalsMode}
                    setShowCombinedRevenue={setShowCombinedRevenue}
                    showCombinedRevenue={showCombinedRevenue}
                    removeMargin={hideControls}
                  />

                  <DecilesTable
                    data={calculatedState.graphData}
                    hidePassbacks={true}
                    includeTotalIntervalRevenue={
                      intervalsMode === 1 &&
                      showCombinedRevenue &&
                      selectedBidder !== 'Combined data'
                    }
                  />
                </Grid>
                <Grid item md={12} lg={12} xl={12}>
                  {calculatedState.cumulativeData.length > 0 && (
                    <>
                      <Grid item xs={12}>
                        <SectionHeader
                          title="Cumulative distribution"
                          tooltip={tooltipConfig.cumulativeDistribution}
                        />
                      </Grid>

                      {calculated && (
                        <CMPvisualiser
                          selectedBidder={selectedBidder}
                          decilesData={pricesReportData?.data}
                          eCPM={eCPM}
                          data={calculatedState.cumulativeData}
                          recalculate={(data, bidder, cpm) => {
                            const cumulativeData = calculateCumulativeDistributionData(
                              data,
                              bidder,
                              cpm
                            );
                            setCalculatedState(prev => ({...prev, cumulativeData}));
                          }}
                        />
                      )}
                    </>
                  )}
                </Grid>
              </Grid>
            </FadeIn>
          </>
        )}
      </Container>
    );
  }
);

export default React.memo(PricesReport);
